import { useRef, useState, type ReactNode } from "react";
import {
  Check,
  GripVertical,
  ImagePlus,
  MoreHorizontal,
  Pencil,
  Pin,
  Play,
  ShieldCheck,
  Trash2,
} from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { cn } from "@/shared/lib/utils";

/**
 * 槽位物料网格(packs W3 从人物详情 SlotSection 抽出,人物库/产品库共用;W5 策展面板继续吃)。
 *
 * 纯展示层:数据、权限与所有变更(上传/排序/迁槽/标注/删除/钉选/红线)全部经 props 注入,
 * 组件内只保留**拖拽的过程态**(dragId/dragOverId —— 它是纯 UI 状态,落点即回调 onReorder
 * 全覆盖 id 序,由调用方提交 PUT order)。菜单里的「前移/后移」是键盘可达的排序主路,
 * 拖拽只是指针增强(a11y 口径与 W2 一致)。
 */
export interface SlotMaterialItem {
  id: number;
  label?: string | null;
  url?: string | null;
  /** 缩略图现签;缺省(存量未回填 / 生成失败)时组件回退 url。 */
  thumbUrl?: string | null;
  /**
   * 素材介质:image | video(0915 归一)。缺省按 image(归一前这张表恒图)。
   *
   * ★必须按它分派渲染:视频素材的 `thumbUrl` **恒为 null**(没有可直接缩略的位图),
   * 当图渲染会得到一张 `naturalWidth===0` 的裂图 —— 窄屏上 alt 文字还会顶出来占一大块。
   * 0916 gstack 实机实测:页面 0 个 video、1 个裂 img,就是这个漏。
   */
  kind?: "image" | "video" | null;
  /** 视频时长秒(ai 侧回填);image 恒 null。视频卡右下角角标用。 */
  durationSec?: number | null;
  /**
   * 槽位中文名。给它即在卡左下角出一枚小角标(0916 人物页平铺版用)。
   *
   * ★这是"槽位从结构降级成属性"的那一步:一屏一个网格看全这个人有什么,
   * 槽只是卡上的一行小字,不再是四个把页面切断的分区标题。
   */
  slotLabel?: string | null;
  /**
   * 该素材当前所在的槽 key。给它即把「移到…」里的**当前槽**那一项去掉 ——
   * 平铺网格里一份菜单服务所有卡,不去掉就会出现一个点了什么也不发生的菜单项。
   */
  slotKey?: string | null;
  brandProtect?: boolean;
}

/**
 * 素材预览(按介质分派)。视频给 `<video>` + 时长角标 + 播放符,图给 `<img>`。
 *
 * <p>视频用 `preload="metadata"`:只拉头部出首帧,不拉整段 —— 一屏十几张卡不能每张都下一个视频。
 * `muted`+`playsInline` 是移动端能显示首帧的前提(iOS 上没有它们会是一块黑)。
 */
function MaterialPreview({ m, alt }: { m: SlotMaterialItem; alt: string }) {
  const isVideo = m.kind === "video";
  const src = isVideo ? m.url : (m.thumbUrl ?? m.url);
  if (!src) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
        预览失效
      </div>
    );
  }
  if (isVideo) {
    return (
      <div className="relative h-full w-full">
        <video
          src={src}
          muted
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
          aria-label={alt}
        >
          {/* 素材预览无字幕轨(a11y 规则占位)。 */}
          <track kind="captions" />
        </video>
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-black/45 text-white">
            <Play className="h-3.5 w-3.5 translate-x-[1px]" />
          </span>
        </span>
        {m.durationSec != null && (
          <span className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/55 px-1 py-0.5 text-2xs font-medium tabular-nums text-white">
            {m.durationSec}s
          </span>
        )}
      </div>
    );
  }
  return (
    <img src={src} alt={alt} draggable={false} className="h-full w-full object-cover" />
  );
}

export interface SlotMoveTarget {
  key: string;
  label: string;
}

export function SlotMaterialsSection({
  title,
  hint,
  materials,
  canWrite = true,
  uploading = false,
  onUpload,
  acceptVideo = false,
  onReorder,
  moveTargets = [],
  onMoveSlot,
  onEditLabel,
  onDelete,
  primaryMaterialId,
  canPin = false,
  onPin,
  pinBadgeText = "主定妆",
  pinActionText = "设为主定妆图",
  onToggleBrandProtect,
  domainAttr,
  selectable = false,
  selectedIds,
  onToggleSelect,
  countUnit = "张",
  toolbar,
  extraActions,
  emptyText,
  uploadText,
}: {
  title: string;
  hint: string;
  materials: SlotMaterialItem[];
  /** 写权限(上传/排序/菜单整体);false = 纯浏览网格。 */
  canWrite?: boolean;
  uploading?: boolean;
  onUpload?: (files: FileList) => void;
  /**
   * 这个槽收不收视频(0915 归一批 B)。identity 槽传 true —— 归一后「身份视频」就是
   * identity 槽里 kind=video 的一条素材,与定妆照同表同槽。
   * ★其余槽保持只收图:表情/姿态/造型槽的视频没有消费面,收下就是"传了没反应"。
   */
  acceptVideo?: boolean;
  /** 落点回调:该槽全部物料的新 id 序(全覆盖语义,调用方原样提交)。 */
  onReorder?: (orderedIds: number[]) => void;
  /** 迁槽目标(不含当前槽);空数组 = 菜单不出「移到…」。 */
  moveTargets?: SlotMoveTarget[];
  onMoveSlot?: (material: SlotMaterialItem, targetKey: string) => void;
  onEditLabel?: (material: SlotMaterialItem) => void;
  onDelete?: (material: SlotMaterialItem) => void;
  /** 钉选(人物 identity 槽专用):primaryMaterialId 出徽标,canPin 出菜单项。 */
  primaryMaterialId?: number | null;
  canPin?: boolean;
  onPin?: (material: SlotMaterialItem) => void;
  pinBadgeText?: string;
  pinActionText?: string;
  /** 红线标记 toggle(产品库专用):提供即出菜单项(标记/取消),徽标始终按数据渲染。 */
  onToggleBrandProtect?: (material: SlotMaterialItem) => void;
  /** e2e 定位钩子属性名(如 data-character-slot / data-product-slot),值 = title。 */
  domainAttr?: string;
  /** W5 策展多选态:整卡变可点选(N 选 M),与写操作互斥(调用方传 canWrite=false)。 */
  selectable?: boolean;
  selectedIds?: ReadonlySet<number>;
  onToggleSelect?: (material: SlotMaterialItem) => void;
  /** 计数单位:图槽「张」,混介质面传「项」(一条视频不叫一"张")。 */
  countUnit?: string;
  /** 标题行下方的整行工具条(如介质筛选 chips)。窄屏不与标题抢行。 */
  toolbar?: ReactNode;
  /** 上传按钮旁的额外动作(如「AI 生成」)。 */
  extraActions?: ReactNode;
  /** 空态文案;缺省用「这个槽还空着 —— {hint}」。 */
  emptyText?: string;
  /** 上传按钮文案;缺省按 acceptVideo 给「传照片 / 视频」或「传图」。 */
  uploadText?: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);

  const reorderTo = (fromId: number, toIndex: number) => {
    const ids = materials.map((m) => m.id);
    const from = ids.indexOf(fromId);
    if (from < 0 || toIndex < 0 || toIndex >= ids.length) return;
    ids.splice(toIndex, 0, ...ids.splice(from, 1));
    onReorder?.(ids);
  };

  const onDrop = (targetId: number) => {
    setDragOverId(null);
    if (dragId == null || dragId === targetId) return;
    reorderTo(
      dragId,
      materials.findIndex((m) => m.id === targetId)
    );
  };

  return (
    <section aria-label={`${title}物料`} {...(domainAttr ? { [domainAttr]: title } : {})}>
      {/*
        ★窄屏两刀(0916 老板「排版不合理」+ 本地视觉校验复现):
        ① 整行 flex-wrap,按钮组 shrink-0 —— 按钮不再压缩标题;
        ② 标题块给 **basis 16rem**(不是裸 flex-1):只给 min-w-0 的话,400px 下
           flex 会把标题块一路压到十来个字宽,hint 被挤成一列竖排字(实测截图当场看到)。
           有了 basis,装不下时按钮组整体换行到下一行,标题永远有一行完整的宽度。
      */}
      <div className="mb-2 flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0 flex-[1_1_16rem]">
          <h3 className="text-sm font-medium">
            {title}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {materials.length} {countUnit}
            </span>
          </h3>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canWrite && onUpload && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <ImagePlus className="mr-2 h-3.5 w-3.5" />
                {uploading ? "上传中…" : (uploadText ?? (acceptVideo ? "传照片 / 视频" : "传图"))}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptVideo ? "image/*,video/mp4,video/quicktime" : "image/*"}
                multiple
                className="hidden"
                aria-label={`上传${title}${acceptVideo ? "素材" : "物料"}`}
                onChange={(e) => {
                  if (e.target.files?.length) onUpload(e.target.files);
                  e.target.value = "";
                }}
              />
            </>
          )}
          {canWrite && extraActions}
        </div>
      </div>

      {toolbar && <div className="mb-3">{toolbar}</div>}

      {materials.length === 0 ? (
        <div className="rounded-[calc(var(--radius)*1.5)] border border-dashed border-border/70 py-8 text-center text-xs text-muted-foreground">
          {emptyText ?? `这个槽还空着 —— ${hint}`}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
          {materials.map((m, i) => (
            <MaterialTile
              key={m.id}
              material={m}
              isPrimary={primaryMaterialId === m.id}
              canPin={canWrite && canPin}
              canWrite={canWrite}
              dragging={dragId === m.id}
              dragOver={dragOverId === m.id && dragId !== m.id}
              onDragStart={() => setDragId(m.id)}
              onDragEnd={() => {
                setDragId(null);
                setDragOverId(null);
              }}
              onDragOver={() => setDragOverId(m.id)}
              onDrop={() => onDrop(m.id)}
              onPin={onPin && (() => onPin(m))}
              onEditLabel={onEditLabel && (() => onEditLabel(m))}
              moveTargets={
                m.slotKey ? moveTargets.filter((t) => t.key !== m.slotKey) : moveTargets
              }
              onMoveSlot={onMoveSlot && ((key) => onMoveSlot(m, key))}
              onNudge={onReorder && ((dir) => reorderTo(m.id, i + dir))}
              canNudge={{ up: i > 0, down: i < materials.length - 1 }}
              onDelete={onDelete && (() => onDelete(m))}
              pinBadgeText={pinBadgeText}
              pinActionText={pinActionText}
              onToggleBrandProtect={onToggleBrandProtect && (() => onToggleBrandProtect(m))}
              selectable={selectable}
              selected={!!selectedIds?.has(m.id)}
              onToggleSelect={onToggleSelect && (() => onToggleSelect(m))}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function MaterialTile({
  material: m,
  isPrimary,
  canPin,
  canWrite,
  dragging,
  dragOver,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onPin,
  onEditLabel,
  moveTargets,
  onMoveSlot,
  onNudge,
  canNudge,
  onDelete,
  pinBadgeText,
  pinActionText,
  onToggleBrandProtect,
  selectable = false,
  selected = false,
  onToggleSelect,
}: {
  material: SlotMaterialItem;
  isPrimary: boolean;
  canPin: boolean;
  canWrite: boolean;
  dragging: boolean;
  dragOver: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: () => void;
  onDrop: () => void;
  onPin?: () => void;
  onEditLabel?: () => void;
  moveTargets: SlotMoveTarget[];
  onMoveSlot?: (targetKey: string) => void;
  /** 缺省(调用方没给 onReorder)即不出「前移/后移」—— 菜单项不许点了没反应。 */
  onNudge?: (dir: -1 | 1) => void;
  canNudge: { up: boolean; down: boolean };
  onDelete?: () => void;
  pinBadgeText: string;
  pinActionText: string;
  onToggleBrandProtect?: () => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}) {
  if (selectable) {
    // 策展多选态:整卡一个真按钮(键盘可达),与写操作互斥 —— 不拖拽、不出菜单。
    return (
      <button
        type="button"
        aria-pressed={selected}
        onClick={onToggleSelect}
        data-testid={`slot-pick-${m.id}`}
        className={cn(
          "group relative overflow-hidden rounded-[calc(var(--radius)*1.5)] border bg-card text-left transition-shadow",
          selected ? "ring-2 ring-primary" : "hover:border-primary/60"
        )}
      >
        <div className="aspect-[3/4] w-full bg-muted">
          {/* 缩略图优先、原图兜底(存量 thumbUrl 未回填不能裂);视频走 video 不走 img。 */}
          <MaterialPreview m={m} alt={m.label || "候选物料"} />
        </div>
        {selected && (
          <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="h-3.5 w-3.5" />
          </span>
        )}
        <div className="px-2 py-1.5">
          <span className="block truncate text-xs text-muted-foreground">
            {m.label || "未标注"}
          </span>
        </div>
      </button>
    );
  }
  const hasMenu =
    canWrite &&
    (onEditLabel || onDelete || (canPin && onPin) || onMoveSlot || onToggleBrandProtect || onNudge);
  return (
    // 拖拽是指针增强;键盘用户走操作菜单里的「前移/后移」(a11y 主路),故这里不挂 role/tabIndex。
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      draggable={canWrite && !!onNudge}
      onDragStart={canWrite && onNudge ? onDragStart : undefined}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        if (!canWrite || !onNudge) return;
        e.preventDefault();
        onDragOver();
      }}
      onDrop={(e) => {
        if (!canWrite || !onNudge) return;
        e.preventDefault();
        onDrop();
      }}
      className={cn(
        "group relative overflow-hidden rounded-[calc(var(--radius)*1.5)] border bg-card",
        dragging && "opacity-40",
        dragOver && "ring-2 ring-primary"
      )}
    >
      <div
        className={cn(
          "relative aspect-[3/4] w-full bg-muted",
          canWrite && onNudge && "cursor-grab active:cursor-grabbing"
        )}
      >
        <MaterialPreview m={m} alt={m.label || "素材"} />
        {/* ★槽角标贴**预览框**底部,不是整张卡底部 —— 卡还包含下面那行标注,
            贴卡就会盖在标注文字上(0916 本地视觉校验当场看到「定妆视频」叠字)。 */}
        {m.slotLabel && (
          <span className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/55 px-1.5 py-0.5 text-2xs font-medium text-white">
            {m.slotLabel}
          </span>
        )}
      </div>

      {isPrimary && (
        <Badge className="absolute left-1.5 top-1.5 gap-1 text-2xs">
          <Pin className="h-3 w-3" />
          {pinBadgeText}
        </Badge>
      )}
      {m.brandProtect && (
        <Badge variant="secondary" className="absolute right-1.5 top-1.5 gap-1 text-2xs">
          <ShieldCheck className="h-3 w-3" />
          品牌保护
        </Badge>
      )}
      <div className="flex items-center gap-1 px-2 py-1.5">
        {onNudge && <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />}
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {m.label || "未标注"}
        </span>
        {hasMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                aria-label="物料操作"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canPin && onPin && !isPrimary && (
                <DropdownMenuItem onClick={onPin}>
                  <Pin className="mr-2 h-3.5 w-3.5" />
                  {pinActionText}
                </DropdownMenuItem>
              )}
              {onEditLabel && (
                <DropdownMenuItem onClick={onEditLabel}>
                  <Pencil className="mr-2 h-3.5 w-3.5" />
                  改标注
                </DropdownMenuItem>
              )}
              {onToggleBrandProtect && (
                <DropdownMenuItem onClick={onToggleBrandProtect}>
                  <ShieldCheck className="mr-2 h-3.5 w-3.5" />
                  {m.brandProtect ? "取消品牌保护" : "标记品牌保护"}
                </DropdownMenuItem>
              )}
              {onNudge && (
                <>
                  <DropdownMenuItem disabled={!canNudge.up} onClick={() => onNudge(-1)}>
                    前移一位
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={!canNudge.down} onClick={() => onNudge(1)}>
                    后移一位
                  </DropdownMenuItem>
                </>
              )}
              {onMoveSlot && moveTargets.length > 0 && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>移到…</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {moveTargets.map((t) => (
                      <DropdownMenuItem key={t.key} onClick={() => onMoveSlot(t.key)}>
                        {t.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={onDelete}
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    删除
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
